import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["promptData"];
import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { useSubscribeToCopilotChannel } from 'copilot-toolkit/channel/hooks/useSubscribeToCopilotChannel';
import { useIsCopilotWidgetActive } from 'copilot-toolkit/hooks/useIsCopilotWidgetActive';
import { useSendCollaborationAction, useSendCollaborationPluginContext } from 'copilot-toolkit/hooks/useSendCollaboration';
import { TOPIC_NAMES } from 'copilot-toolkit/types/Channel';
import { useCallback, useEffect, useMemo } from 'react';
export const useCollaborationSession = ({
  onCopilotAction,
  onGetCurrentSessionContext,
  sessionId,
  appName,
  plugins,
  plugin
}) => {
  const pluginName = useMemo(() => plugin || (plugins === null || plugins === void 0 ? void 0 : plugins[0]) || '', [plugin, plugins]);
  const isCopilotWidgetActive = useIsCopilotWidgetActive();
  const sendCollaborationPluginContext = useSendCollaborationPluginContext();
  const sendCollaborationAction = useSendCollaborationAction();
  const sendToCopilotFromParentApp = useSendOnCopilotChannel(TOPIC_NAMES.COLLABORATE_PARENT_APP_MESSAGE);
  const promptUserForMoreInfo = useSendOnCopilotChannel(TOPIC_NAMES.PROMPT_USER_FOR_MORE_INFO);
  useEffect(() => {
    if (isCopilotWidgetActive && appName && sessionId && pluginName) {
      sendToCopilotFromParentApp({
        payload: {
          plugin: pluginName,
          appName,
          sessionId
        }
      });
    }
    return () => {
      if (pluginName && appName) {
        sendToCopilotFromParentApp({
          payload: {
            plugin: pluginName,
            appName,
            sessionId: ''
          }
        });
      }
    };
  }, [appName, isCopilotWidgetActive, pluginName, plugins, sendToCopilotFromParentApp, sessionId]);
  const sendCollaborationStateChangeFromAppToCopilot = useCallback(collaborationState => {
    sendToCopilotFromParentApp({
      payload: {
        parentAppState: collaborationState,
        sessionId,
        plugin: pluginName
      }
    });
  }, [pluginName, sendToCopilotFromParentApp, sessionId]);
  const sendActionFromAppToCopilot = useCallback((messageAction, messageMetaInfo) => {
    if (!messageAction) {
      consoleIfNotProd.warn('No message action provided');
      return;
    }
    const {
        promptData
      } = messageAction,
      messageActionWithoutPrompt = _objectWithoutPropertiesLoose(messageAction, _excluded);
    if (promptData) {
      promptUserForMoreInfo({
        messageAction: messageActionWithoutPrompt,
        messageMetaInfo: Object.assign({}, messageMetaInfo, {
          source: 'PLUGIN',
          type: 'IN-APP'
        }),
        promptData
      });
    } else {
      sendCollaborationAction({
        messageAction,
        messageMetaInfo: Object.assign({}, messageMetaInfo, {
          source: 'PLUGIN',
          type: 'IN-APP'
        })
      });
    }
  }, [promptUserForMoreInfo, sendCollaborationAction]);
  useSubscribeToCopilotChannel(TOPIC_NAMES.COLLABORATE_COPILOT_ACTON, onCopilotAction);
  const handleOnGetCurrentSessionContext = useCallback(({
    requestSessionsId
  }) => {
    if (requestSessionsId !== sessionId) {
      return;
    }
    Promise.resolve(onGetCurrentSessionContext()).then(collaborationContextObject => {
      if (!collaborationContextObject) {
        consoleIfNotProd.warn(`No context found for appName: ${appName} session: ${sessionId}`);
        sendCollaborationPluginContext({
          plugin: pluginName,
          extraIntentProperties: {}
        });
        return;
      }
      // TODO: Remove this once all plugins are updated
      if (typeof collaborationContextObject === 'string') {
        // Otherwise its a string (legacy means of passing)
        sendCollaborationPluginContext({
          plugin: pluginName,
          extraIntentProperties: {}
        });
        return;
      }
      const extraIntentProperties = Object.assign({}, (_objectDestructuringEmpty(collaborationContextObject), collaborationContextObject));
      sendCollaborationPluginContext({
        plugin: pluginName,
        extraIntentProperties
      });
    }).catch(() => {
      consoleIfNotProd.error('Error getting current session context');
      sendCollaborationPluginContext({
        plugin: pluginName,
        extraIntentProperties: {}
      });
    });
  }, [appName, onGetCurrentSessionContext, pluginName, sendCollaborationPluginContext, sessionId]);
  useSubscribeToCopilotChannel(TOPIC_NAMES.COLLABORATE_COPILOT_GET_CONTEXT, handleOnGetCurrentSessionContext);
  return {
    sendActionFromAppToCopilot,
    sendCollaborationStateChangeFromAppToCopilot,
    isCopilotReady: isCopilotWidgetActive
  };
};